import { Button } from "@component/button";
import CountryCodeSelect from "@component/country-code-select";
import { COUNTRY_CODES, CountryCode } from "@component/country-codes-data";
import { Input } from "@component/input";
import { maxFileSize } from "@constant/app";
import { useUserProfileContext } from "@context/user_profile";
import { authOTPJobSeeker, authRegisterWithoutPassword } from "@service/auth";
import { getMyCV, uploadCV, uploadTempCV } from "@service/cv";
import { Job } from "@type/job";
import { setCookie } from "@util/cookie";
import { trackEvent } from "@util/mixpanel";
import { catchErrorLog } from "@util/sentry";
import { isValidLocalPhone, localPhoneNumber, MY_MOBILE_ERROR } from "@util/phone";
import { parsePhoneNumber } from "@util/string";
import { cn } from "@util/tailwind";
import { buildE164, isValidE164 } from "@service/whatsapp";
import { AlertCircleIcon, CheckIcon, PaperclipIcon, XIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
    isPreviewMode?: boolean;
    isGuestMode: boolean;
    isQuickApply?: boolean;
    job: Job
    onReloadCurrentUser: (onGetCV: (user_id: string, email: string, name: string) => void) => void
    onJobApplied?: (isApplied: boolean) => void
}

export default function ApplyForm(props: Props) {
    const selectedCountryData = COUNTRY_CODES.find((c) => c.name === (props.job.location_metadata?.country || "United States"));
    const navigate = useNavigate();
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isLoadingApply, setIsLoadingApply] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState(false);
    const [cvFileName, setCVFileName] = useState<string>("");
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [dataForm, setDataForm] = useState({
        user_id: "",
        name: "",
        email: "",
        country_code: selectedCountryData?.code || COUNTRY_CODES[0].code || "+1",
        phone: "",
        linkedin: "",
    });
    const [selectedCountry, setSelectedCountry] = useState<CountryCode | undefined>(selectedCountryData);
    // The phone error shows once the candidate leaves the field (or it was filled
    // from their CV), not while they are still typing.
    const [phoneTouched, setPhoneTouched] = useState<boolean>(false);
    // What is submitted: +60 drops a repeated 60 and the leading 0 (#4933).
    const countryCode = selectedCountry?.code || "+1";
    const localPhone = localPhoneNumber(countryCode, dataForm.phone);
    const phoneValid = isValidLocalPhone(countryCode, localPhone);
    const showPhoneError = phoneTouched && !!dataForm.phone && !phoneValid;
    // WhatsApp opt-in capture (#2857) — consent + a valid E.164 phone are the
    // compliance gate for any downstream WhatsApp send. Only the guest quick-apply
    // path creates the candidate here (candidate_id available), so scope the capture
    // to it; other apply paths persist downstream (follow-up) and showing the box
    // there would silently drop the consent.
    // Also gated on the job's company having WhatsApp messaging enabled
    // (`company_whatsapp_enabled` from /jobs/detail + /jobs/open-detail) — no
    // consent capture for companies that can't send WhatsApp anyway, and the BE
    // only enables it where the WhatsApp routes are live (this replaced the old
    // VITE_WHATSAPP_OPTIN_ENABLED env ship-gate). Absent field = hidden.
    const supportsWhatsappOptIn =
        props.isGuestMode &&
        !!props.isQuickApply &&
        !!props.job.company_whatsapp_enabled;
    const phoneE164 = buildE164(countryCode, localPhone);
    // AUTO opt-in (no checkbox — product decision): every quick-apply candidate
    // of a WhatsApp-enabled company gets WhatsApp updates. Best-effort: an
    // invalid E.164 phone doesn't block the application, it just skips WhatsApp.
    const autoWhatsappOptIn = supportsWhatsappOptIn && isValidE164(phoneE164);

    useEffect(() => {
        if (props.isGuestMode) return;

        setLoading(true);
        props.onReloadCurrentUser((user_id: string, email: string, name: string) => {
            return getMyCV().then((resp) => {
                setCVFileName(resp?.data?.filename || "");
                const phoneData = parsePhoneNumber(`${resp?.data?.CV_data.country_code}${resp?.data?.CV_data.phone}`)
                setSelectedCountry(selectedCountryData)
                setDataForm({
                    user_id: user_id,
                    name: name,
                    email: email,
                    country_code: selectedCountryData?.code || COUNTRY_CODES[0].code,
                    phone: phoneData.number,
                    linkedin: "",
                });
                setPhoneTouched(true);
                setLoading(false);
            })
        });
    }, [])

    const validateFile = (file: File): string | null => {
        if (file.size > maxFileSize) {
            return "File size must be less than 5MB";
        }
        return null;
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files === null || e.target.files.length === 0) {
            return;
        }

        const file = e.target.files[0];
        const error = validateFile(file);
        if (error) {
            return;
        }

        setResumeFile(file);
        setIsUploading(true);

        try {
            if (!props.isGuestMode && cvFileName === "") {
                const response = await uploadCV(file);
                if (response.success) {
                    trackEvent('upload_cv', {
                        resume_uploaded: true
                    })
                    props.onReloadCurrentUser((user_id: string, email: string, name: string) => {
                        return getMyCV().then((resp) => {
                            setCVFileName(resp?.data?.filename || "");
                            const phoneData = parsePhoneNumber(resp?.data?.CV_data.phone || "")
                            setSelectedCountry(selectedCountryData)
                            setDataForm({
                                user_id: user_id,
                                name: name,
                                email: email,
                                country_code: selectedCountryData?.code || COUNTRY_CODES[0].code,
                                phone: phoneData.number,
                                linkedin: "",
                            });
                            setPhoneTouched(true);
                        })
                    });
                } else {
                    throw new Error(response.message);
                }
                return
            }

            const uploadResponse = await uploadTempCV(file);
            if (!uploadResponse.success) {
                throw new Error(uploadResponse.message || "Failed to upload CV");
            }

            if (!uploadResponse.data) {
                throw new Error("Only PDF files are accepted. Please upload your resume as a PDF");
            }

            if (!uploadResponse.data.filename) {
                throw new Error("Upload response missing filename");
            }

            const cvUuid = uploadResponse.data.filename;
            trackEvent('upload_cv', {
                resume_uploaded: true
            })
            sessionStorage.setItem("__TEMP_CVID__", cvUuid);
            setIsUploading(false);
        } catch (error) {
            catchErrorLog(`Error during upload/registration: ${error}`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        setResumeFile(null);
        sessionStorage.removeItem("__TEMP_CVID__");
    };

    const onChangeDataForm = (field: string, value: string) => {
        setDataForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    }

    const handleSubmit = useCallback(() => {
        trackEvent('apply_now_clicked', {
            job_id: props.job.id,
            job_title: props.job.job_title,
            company_name: props.job.company_name,
        })

        if (props.isGuestMode && props.isQuickApply) {
            setIsLoadingApply(true);
            const splitName = dataForm.name.trim().split(" ");
            const firstName = splitName.shift() || "";
            const lastName = splitName.join(" ");
            authRegisterWithoutPassword({
                first_name: firstName,
                last_name: lastName,
                email: dataForm.email,
                phone_number: localPhone,
                country_code: selectedCountry?.code || "+1",
                job_id: props.job.id,
                referral_code: props.job.company_referrer_code || null,
                cv_filename: resumeFile?.name || "",
                // Auto WhatsApp opt-in (#2857) — every quick-apply candidate of a
                // WhatsApp-enabled company, phone permitting.
                whatsapp_opt_in: autoWhatsappOptIn,
            }).then((resp) => {
                if (resp.status === 201 || resp.status === 200) {
                    sessionStorage.setItem("__USERID__", resp.data.data.id);
                    // Carry the WhatsApp opt-in intent + phone forward; it is persisted
                    // after OTP verification (candidate-email-otp-verify), where a token
                    // exists — the opt-in endpoint needs one (#2857).
                    setCookie("__apply_job_guest__", JSON.stringify({
                        ...dataForm,
                        phone: localPhone,
                        country_code: selectedCountry?.code || "+1",
                        whatsapp_opt_in: autoWhatsappOptIn,
                        phone_e164: phoneE164,
                        // Scope the opt-in to THIS candidate so a stale cookie can't
                        // attribute consent to a later, unrelated OTP verification
                        // (#2857 review). Stringified to match __USERID__.
                        whatsapp_candidate_id: String(resp.data.data.id),
                    }));
                    trackEvent('auth_register_without_password', {
                        email: resp.data.data.email,
                        job_id: props.job.id,
                    })
                    authOTPJobSeeker(resp.data.data.email).finally(() => {
                        let urlCandidate = `/quick-apply/${props.job.id}`;
                        if (props.job.company_referrer_code) {
                            urlCandidate += `?referral=${props.job.company_referrer_code}`;
                        }
                        navigate(urlCandidate);
                    }).finally(() => {
                        setIsLoadingApply(false);
                    })
                }
            }).catch((error) => {
                if (error?.response?.data?.data?.is_job_applied) {
                    if (props.onJobApplied) {
                        props.onJobApplied(true);
                    }
                    setErrorMessage(error?.response?.data?.message || "You have already applied for this job.");
                } else if (error?.response?.data?.data?.is_user_exist) {
                    setErrorMessage(error?.response?.data?.message || "An account with this email already exists.");
                } else if (error?.response?.data?.data?.is_cv_exist) {
                    setErrorMessage(error?.response?.data?.message || "A CV with the same name already exists.");
                } else {
                    catchErrorLog(`Error during guest apply registration: ${error}`);
                }
                setIsLoadingApply(false);
            })
        } else if (props.isGuestMode && !props.isQuickApply) {
            setCookie("__apply_job_guest__", JSON.stringify({
                ...dataForm,
                phone: localPhone,
                country_code: selectedCountry?.code || "+1",
                whatsapp_opt_in: autoWhatsappOptIn,
                phone_e164: phoneE164,
            }));
            let urlCandidate = `/candidate-signup?ref=${props.job.id}`;
            if (props.job.company_referrer_code) {
                urlCandidate += `&referral=${props.job.company_referrer_code}`;
            }

            trackEvent('auth_prompt_shown', {
                prompt_type: "onboarding_flow"
            })
            navigate(urlCandidate);
        } else {
            trackEvent('auth_prompt_shown', {
                prompt_type: "apply_flow"
            })
            navigate(`/apply/${props.job.id}`);
        }
    }, [props, dataForm, selectedCountry, navigate, resumeFile, phoneE164, localPhone])

    if (isLoadingUserProfile) return <></>

    return (
        <>
            <div className="grid grid-cols-1 gap-4">
                <div>
                    {isLoading ? (
                        <div className="animate-pulse space-y-2">
                            <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
                        </div>
                    ) : (
                        <Input
                            id="name"
                            placeholder="Name*"
                            value={dataForm.name}
                            disabled={!!props.job.has_applied || (!props.isGuestMode && cvFileName === "") || props.job.job_status === "Paused"}
                            onChange={(e) => onChangeDataForm("name", e.target.value)}
                            className={props.job.has_applied ? "bg-muted text-foreground" : ""}
                        />
                    )}
                </div>
                <div>
                    {isLoading ? (
                        <div className="animate-pulse space-y-2">
                            <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
                        </div>
                    ) : (
                        <Input
                            id="email"
                            placeholder="Email*"
                            value={dataForm.email}
                            disabled={!!props.job.has_applied || (!props.isGuestMode && cvFileName === "") || props.job.job_status === "Paused"}
                            onChange={(e) => onChangeDataForm("email", e.target.value)}
                            className={props.job.has_applied ? "bg-muted text-foreground" : ""}
                        />
                    )}
                </div>
                <div className="flex">

                    {isLoading ? (
                        <div className="animate-pulse space-y-2 w-full">
                            <div className="h-9 bg-gray-100 dark:bg-gray-700 rounded"></div>
                        </div>
                    ) : (
                        <>
                            <CountryCodeSelect
                                value={selectedCountry ?? COUNTRY_CODES[0]}
                                disabled={!!props.job.has_applied || (!props.isGuestMode && cvFileName === "") || props.job.job_status === "Paused"}
                                onChange={setSelectedCountry}
                            />
                            <Input
                                id="phone"
                                placeholder="Phone number*"
                                value={dataForm.phone}
                                className={props.job.has_applied ? "bg-muted text-foreground rounded-l-none" : "rounded-l-none"}
                                disabled={!!props.job.has_applied || (!props.isGuestMode && cvFileName === "") || props.job.job_status === "Paused"}
                                type="tel"
                                maxLength={18}
                                aria-invalid={showPhoneError}
                                aria-describedby={showPhoneError ? "phone-error" : undefined}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    onChangeDataForm("phone", value);
                                }}
                                onBlur={() => {
                                    setPhoneTouched(true);
                                    // Show the number that will be submitted (60194638952 → 194638952).
                                    if (phoneValid && localPhone !== dataForm.phone) {
                                        onChangeDataForm("phone", localPhone);
                                    }
                                }}
                            />
                        </>
                    )}
                </div>
                {showPhoneError && (
                    <p id="phone-error" className="-mt-2 text-sm text-red-600">
                        {countryCode === "+60" ? MY_MOBILE_ERROR : "Enter a valid phone number."}
                    </p>
                )}
            </div>

            <div className="space-y-2">
                {isLoading ? (
                    <div className="animate-pulse space-y-2">
                        <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded"></div>
                    </div>
                ) : (
                    <Input
                        id="linkedin"
                        placeholder="LinkedIn (optional)"
                        disabled={!!props.job.has_applied || (!props.isGuestMode && cvFileName === "")}
                        onChange={(e) => onChangeDataForm("linkedin", e.target.value)}
                        className={props.job.has_applied ? "bg-muted text-foreground" : ""}
                    />
                )}
            </div>

            {isLoading ? (
                <div className="animate-pulse space-y-2">
                    <div className="h-24 bg-gray-100 dark:bg-gray-700 rounded"></div>
                </div>
            ) : (
                <div className="border border-dashed rounded-md p-4 text-center relative">
                    {!props.isPreviewMode && (props.isGuestMode || (cvFileName === "" && userInfo?.role === "job_seeker" && !resumeFile && !props.job.has_applied)) && (
                        <input
                            type="file"
                            id="resume"
                            className={cn("absolute inset-0 w-full h-full opacity-0", {
                                "cursor-pointer": !props.isPreviewMode
                            })}
                            onChange={handleUpload}
                            accept=".pdf,.docx"
                        />
                    )}

                    <div className={cn("flex flex-col items-center gap-1", {
                        "cursor-pointer": !props.isPreviewMode
                    })}>
                        <PaperclipIcon className="h-5 w-5 text-muted-foreground" />
                        <p className="text-sm font-medium">
                            Upload your CV
                        </p>
                        {!isLoading && (
                            <>
                                {(cvFileName === "" && isUploading) && (
                                    <div className="flex items-center gap-2 px-2 py-4">
                                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span className="text-sm">Processing</span>
                                    </div>
                                )}
                                {(!props.isGuestMode && cvFileName === "" && userInfo?.role === "job_seeker" && !isUploading && !props.job.has_applied) && (
                                    <div className=" text-red-500 p-2 flex items-start justify-start gap-1">
                                        <AlertCircleIcon className="h-8 w-8 text-red-500" />
                                        <span className="text-sm">
                                            We need your resume to proceed.<br />
                                            Please upload your latest resume in the Resume tab before applying.
                                        </span>
                                    </div>
                                )}
                            </>
                        )}

                        {(props.isGuestMode || cvFileName === "") ? (
                            <>
                                {resumeFile && (
                                    <p className="text-xs text-muted-foreground">
                                        {resumeFile?.name}
                                    </p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                    {resumeFile ? (
                                        `${(resumeFile?.size / 1024 / 1024).toFixed(2)} MB`
                                    ) : (
                                        <>
                                            Supported format: PDF &amp; DOCX only (max 5MB)
                                        </>
                                    )}
                                </p>
                            </>
                        ) : (
                            <>
                                {cvFileName !== "" && (
                                    <p className="text-xs text-muted-foreground">
                                        {cvFileName}
                                    </p>
                                )}
                            </>
                        )}

                        {(props.isGuestMode && resumeFile) && (
                            <div className="flex justify-center space-x-2 mt-2">
                                <button
                                    onClick={handleRemoveFile}
                                    className="px-4 py-1 border border-gray-300 dark:border-gray-600 rounded-full text-sm flex items-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    disabled={isUploading}
                                >
                                    <XIcon className="h-4 w-4 mr-1" />
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <Button
                disabled={(props.isGuestMode && !resumeFile) || isLoadingApply || isUploading || !dataForm.name || !dataForm.email || !dataForm.phone || !phoneValid || (props.job?.has_applied || false) || (!props.isGuestMode && cvFileName === "") || props.job.job_status === "Paused" || props.isPreviewMode || !props.job}
                onClick={handleSubmit}
                variant="default"
                size="lg"
                className="text-base w-full"
            >
                {props.job.has_applied ? (
                    <div className="flex items-center gap-2">
                        <CheckIcon className="h-4 w-4" />
                        <span>Application Submitted</span>
                    </div>
                ) : isLoadingApply ? (
                    "Applying..."
                ) : (
                    "Apply Now"
                )}
            </Button>

            {errorMessage && (
                <div className="text-sm text-center text-red-600 font-medium">
                    {errorMessage}
                </div>
            )}
        </>
    )
}